/**
 * JSON canónico y hash (ADR-04). Lo que se firma es el hash del contenido canónico, así que
 * esta serialización tiene que ser determinista: mismas claves en el mismo orden, sin
 * espacios, sin `undefined`. Corre igual en navegador y en Node (Web Crypto).
 *
 * Solo acepta JSON puro: objetos planos, arreglos, texto, números finitos, booleanos y null.
 * Cualquier otra cosa (Date, Map, Set, instancias de clase, bigint, funciones) lanza en vez de
 * serializarse a algo ambiguo: dos contenidos distintos jamás deben dar el mismo hash.
 */

export function canonizar(valor: unknown): string {
  return serializar(valor, "$");
}

function serializar(valor: unknown, ruta: string): string {
  if (valor === null) return "null";
  switch (typeof valor) {
    case "string":
      return JSON.stringify(valor);
    case "boolean":
      return valor ? "true" : "false";
    case "number":
      if (!Number.isFinite(valor)) throw new TypeError(`Número no finito en ${ruta}`);
      return JSON.stringify(valor);
    case "object": {
      if (Array.isArray(valor)) {
        return `[${valor.map((v, i) => serializar(v === undefined ? null : v, `${ruta}[${i}]`)).join(",")}]`;
      }
      if (!esObjetoPlano(valor)) {
        throw new TypeError(`Solo se aceptan objetos planos; ${nombreDe(valor)} en ${ruta}`);
      }
      const obj = valor as Record<string, unknown>;
      const claves = Object.keys(obj)
        .filter((k) => obj[k] !== undefined)
        .sort();
      const partes = claves.map(
        (k) => `${JSON.stringify(k)}:${serializar(obj[k], `${ruta}.${k}`)}`,
      );
      return `{${partes.join(",")}}`;
    }
    default:
      throw new TypeError(`Valor no serializable (${typeof valor}) en ${ruta}`);
  }
}

function esObjetoPlano(valor: object): boolean {
  const proto = Object.getPrototypeOf(valor);
  return proto === null || proto === Object.prototype;
}

function nombreDe(valor: object): string {
  return valor.constructor?.name ?? "objeto";
}

const codificador = new TextEncoder();

export async function sha256Hex(texto: string | Uint8Array): Promise<string> {
  const datos = typeof texto === "string" ? codificador.encode(texto) : texto;
  // Copia a un ArrayBuffer propio: Web Crypto no acepta vistas sobre SharedArrayBuffer.
  const buffer = datos.buffer.slice(
    datos.byteOffset,
    datos.byteOffset + datos.byteLength,
  ) as ArrayBuffer;
  const digest = await globalThis.crypto.subtle.digest("SHA-256", buffer);
  return bytesAHex(new Uint8Array(digest));
}

export function bytesAHex(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}
